import React, { memo, useState } from 'react';

import { Button, CollapsibleBlock, Confirm, Modal, SpinnerOverlay } from '@components/common';
import CreateCartRequestsForm from '@components/ModalComponents/CreateCartRequestsForm/CreateCartRequestsForm';

import { uuid } from '@utils/common';

import { ISendAssetCartRequestDTO } from '@interfaces/rent/dto/assetCartDTO';

import { MODAL_WIDTH_455 } from '@constants/modals';

import CollapsibleBlockLabel from './CollapsibleBlockLabel';

import styles from './TotalAssetsInformation.module.scss';

interface ITotalAssetsInformation {
	availableAssetRequestCount: number;
	unavailableAssetRequestCount: number;
	assetOwners: Array<{ ownerName: string; assetCount: number }>;
	numberOfFutureRequests: number;
	handleSendAssetCartRequest: ({ comments }: ISendAssetCartRequestDTO) => void;
	handleClearAssetCartRequest: () => void;
	isLoading: boolean;
}

const TotalAssetsInformation = ({
	availableAssetRequestCount,
	unavailableAssetRequestCount,
	assetOwners,
	numberOfFutureRequests,
	handleSendAssetCartRequest,
	handleClearAssetCartRequest,
	isLoading,
}: ITotalAssetsInformation) => {
	const [isOpenCollapsibleBlock, setIsOpenCollapsibleBlock] = useState(false);
	const [isCommentsForm, setIsCommentsForm] = useState(false);
	const [isClearCartConfirm, setIsClearCartConfirm] = useState(false);

	const toggleClearCartConfirm = () => setIsClearCartConfirm(prev => !prev);

	const toggleCollapsibleBlock = () => setIsOpenCollapsibleBlock(prevState => !prevState);

	const toggleCommentsForm = () => setIsCommentsForm(!isCommentsForm);

	const openCommentsForm = () => setIsCommentsForm(true);

	const closeCommentsForm = () => setIsCommentsForm(false);

	const clearHandler = () => {
		handleClearAssetCartRequest();
		toggleClearCartConfirm();
	};

	return (
		<div className={styles.totalAssetsInformationContainer}>
			{isLoading && <SpinnerOverlay />}
			<span className={styles.totalAssetsTitle}>Total assets</span>
			<div className={styles.totalAssetsInformation}>
				<div className={styles.info}>
					<span>Unavailable: </span>
					<span className={styles.unavailableAssetsNumber}>
						{unavailableAssetRequestCount}
					</span>
				</div>
				<div className={styles.info}>
					<span>Available: </span>
					<span className={styles.availableAssetsNumber}>
						{availableAssetRequestCount}
					</span>
				</div>
				<div className={styles.info}>
					<span>Assets owners: </span>
					<span className={styles.assetOwnersNumber}>
						{assetOwners.length}
					</span>
				</div>
			</div>
			<div className={styles.collapsibleBlock}>
				<CollapsibleBlock
					isOpen={isOpenCollapsibleBlock}
					label={
						<CollapsibleBlockLabel
							label={`Assets per request (${numberOfFutureRequests})`}
						/>
					}
					name='numberOfRequests'
					toggleState={toggleCollapsibleBlock}
				>
					<div className={styles.outgoingRequestsContainer}>
						{assetOwners.map(assetOwner => (
							<div
								key={uuid()}
								className={styles.requestOwner}
							>
								<span>{`${assetOwner?.ownerName}: `}</span>
								<span className={styles.requestOwnerCounter}>{assetOwner?.assetCount}</span>
							</div>
						))}
					</div>
				</CollapsibleBlock>
			</div>
			<div className={styles.buttonContainer}>
				<Button
					className={styles.clearAllButton}
					large
					onClick={toggleClearCartConfirm}
					text='Clear all'
					withoutBackground
				/>
				<Button
					disabled={!numberOfFutureRequests}
					large
					onClick={openCommentsForm}
					text={`Create requests (${numberOfFutureRequests})`}
				/>
			</div>
			<Modal
				isOpen={isClearCartConfirm}
				onClose={toggleClearCartConfirm}
				title='Are you sure about this?'
				width='520px'
			>
				<Confirm
					isLoading={isLoading}
					negativeBtnText='Cancel'
					onClose={toggleClearCartConfirm}
					onSubmit={clearHandler}
					positiveBtnText='Ok'
					question='This action will remove all Assets from your Request list.'
				/>
			</Modal>
			<Modal
				isOpen={isCommentsForm}
				onClose={toggleCommentsForm}
				title='Add comments to the request'
				width={MODAL_WIDTH_455}
			>
				<CreateCartRequestsForm
					closeModal={closeCommentsForm}
					handleSendAssetCartRequest={handleSendAssetCartRequest}
					isSingleRequest={numberOfFutureRequests === 1}
				/>
			</Modal>
		</div>
	);
};

export default memo(TotalAssetsInformation);
